/**
 * suggestedPrompts — catálogo CURADO de sugerencias para el empty-state del chat de
 * Paulina (FIX B). Cada chip muestra `label` (texto corto y accionable que ve el
 * comercio) y envía `prompt` (el mensaje real que recibe Paulina).
 *
 * v1 deliberadamente SIMPLE: lista fija, no contextual por página. NO incluye "hablar con
 * soporte" (contradice el propósito de quitarle trabajo a soporte) ni chips que presuman
 * estado del negocio. Mantenerla corta (~5-6) para no abrumar el saludo.
 *
 * Variantes por ROL (gating de UX, espejo del gating del backend):
 * - ROOT (dueño): catálogo data-oriented — Paulina tiene acceso a las tools de datos
 *   (ver mis pedidos, revisar el menú, etc.). `SUGGESTED_PROMPTS`.
 * - NO-ROOT (staff: cajera, encargado de menú…): el backend lo limita a MODO AYUDA (sin
 *   tools de datos). Mostrarle chips data-oriented lo lleva a un "no tengo acceso a esos
 *   datos" — mal UX. Catálogo help-oriented (cómo-hacer, que SÍ resuelve en modo ayuda):
 *   `STAFF_SUGGESTED_PROMPTS`.
 *
 * Selección con `getSuggestedPrompts(isRoot)`.
 */

export interface SuggestedPrompt {
  /** Texto visible en el chip (corto, orientado a la acción del comercio). */
  label: string;
  /** Mensaje que se envía a Paulina al hacer click. */
  prompt: string;
}

// ROOT (dueño): mezcla de datos + ayuda. Paulina tiene las tools de datos para resolverlos.
export const SUGGESTED_PROMPTS: readonly SuggestedPrompt[] = [
  { label: 'Ver mis pedidos de hoy', prompt: '¿cuántos pedidos llevo hoy?' },
  { label: 'Revisar mi menú', prompt: 'revisa mi menú' },
  { label: '¿Cómo cambio mis horarios?', prompt: '¿cómo cambio mis horarios?' },
  { label: '¿Cómo recibo más pedidos?', prompt: '¿cómo puedo recibir más pedidos?' },
  { label: '¿Cómo activo el cobro en línea?', prompt: '¿cómo activo el cobro en línea?' },
];

// NO-ROOT (staff): 100% help-oriented (cómo-hacer). NADA de "ver mis pedidos/ventas": el
// backend limita al no-root a modo ayuda, sin tools de datos, así que esos chips llevarían a
// un "no tengo acceso". Estos chips SÍ se resuelven con la guía de modo ayuda.
export const STAFF_SUGGESTED_PROMPTS: readonly SuggestedPrompt[] = [
  { label: '¿Cómo cambio un precio?', prompt: '¿cómo cambio el precio de un producto?' },
  { label: '¿Cómo agrego un producto?', prompt: '¿cómo agrego un producto a mi menú?' },
  { label: '¿Cómo configuro mis horarios?', prompt: '¿cómo configuro mis horarios?' },
  { label: '¿Cómo comparto mi menú?', prompt: '¿cómo comparto mi menú?' },
];

// Devuelve el catálogo de chips según el rol del usuario. Solo el root explícito (isRoot ===
// true) ve el catálogo data-oriented; cualquier otro caso (staff, o isRoot indefinido mientras
// la suscripción aún no hidrata) cae al catálogo help-oriented, que es seguro para todos.
export const getSuggestedPrompts = (isRoot: boolean | undefined): readonly SuggestedPrompt[] =>
  isRoot === true ? SUGGESTED_PROMPTS : STAFF_SUGGESTED_PROMPTS;
